import { Form, FormikProvider, FormikValues, useFormik } from "formik";
import { Input, Select, Upload } from "../../../custom";
import styles from "../../../components/onboardingSteps/styles.module.scss";
import { FC, useEffect, useState } from "react";
//import { ReactComponent as Image } from "../../../assets/image.svg";
import { mixed, object, string } from "yup";
import { App, Spin, Table } from "antd";
import { ReactComponent as Trash } from "../../../assets/trash.svg";
import { ReactComponent as Edit } from "../../../assets/pencil-paper.svg";
import { useQueries } from "@tanstack/react-query";
import { getGrades, getSubjects } from "../../../utils/request";
import {
  isPdfOrImageFile,
  PDF_IMAGE_ACCEPT,
  PDF_IMAGE_FORMAT_LABELS,
} from "../../../utils/fileValidation";

interface ComponentProps {
  qualification?: Qualification;
  isLoading: boolean;
  certificateUrl?: string;
  isEditing?: boolean;
  onSubmit: (values: FormikValues, resetForm: () => void) => void;
}

interface FormSubject {
  subject: string;
  grade: string;
}

const QualificationForm: FC<ComponentProps> = ({
  certificateUrl,
  qualification,
  isLoading,
  isEditing,
  onSubmit,
}) => {
  const { notification } = App.useApp();
  const [subjects, setSubjects] = useState<FormSubject[]>(
    Array.from(qualification?.subjects!, (subject, index) => {
      return { subject: subject.subject, grade: subject.grade };
    }) || []
  );
  const [isEditingSubject, setIsEditingSubject] = useState(false);
  const [subjectToEdit, setSubjectToEdit] = useState<FormSubject>({
    subject: "",
    grade: "",
  });
  const [imageUrl, setImageUrl] = useState("");

  const queries = useQueries({
    queries: [
      { queryKey: ["get-all-subject"], queryFn: getSubjects },
      { queryKey: ["get-all-grades"], queryFn: getGrades },
    ],
  });

  const [subjectQuery, gradeQuery] = queries;

  const {
    data: subjectData,
    isLoading: isSubjectLoading,
    isError: isSubjectError,
    error: subjectError,
  } = subjectQuery;

  const {
    data: gradeData,
    isLoading: isGradeLoading,
    isError: isGradeError,
    error: gradeError,
  } = gradeQuery;

  const subject = subjectData?.data ?? [];
  const grades = gradeData?.data ?? [];

  const documentName = (str: string) =>
    !!str ? str.slice(str.lastIndexOf("/") + 1) : str;

  const qualSubjects = qualification?.subjects as Subject[];

  useEffect(() => {
    setImageUrl(certificateUrl ?? "");
  }, [certificateUrl]);

  // const handleRemoveImageUrl = () => {
  //   setImageUrl("");
  // };

  const columns = [
    {
      title: "#",
      dataIndex: "index",
      render: (_: any, _i: any, index: number) => index + 1,
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "Grade",
      dataIndex: "grade",
      key: "grade",
    },
    {
      title: "",
      dataIndex: "grade",
      key: "grade",
      render: (_: string, record: FormSubject) => (
        <div className="small-gap">
          <button
            type="button"
            onClick={() => {
              setIsEditingSubject(true);
               setSubjectToEdit(record);
              formik.setFieldValue("subject", record?.subject);
              formik.setFieldValue("grade", record?.grade);
            }}
            className={styles.transparentBtn}
          >
            <Edit />
          </button>
          <button
            type="button"
            onClick={() =>
              setSubjects(
                subjects?.filter(
                  (x) =>
                    x.grade !== record?.grade || x.subject !== record?.subject
                )
              )
            }
            className={styles.transparentBtn}
          >
            <Trash />
          </button>
        </div>
      ),
    },
  ];

  const handleAddSubject = () => {
    const hasValue = !!formik.values.subject && !!formik.values.grade;
    const alreadyExists = subjects?.some(
      (x) => x.subject === formik.values.subject
    );
    if (hasValue) {
      if (alreadyExists && !isEditingSubject) {
        notification.error({
          message: "Subject already exists",
        });
      } else {
        if (isEditingSubject) {
          let index = subjects?.findIndex(
            (x) =>
              x.subject === subjectToEdit?.subject &&
              x.grade === subjectToEdit?.grade
          );
          if (index !== -1) {
            subjects[index].subject = formik.values.subject;
            subjects[index].grade = formik.values.grade;
          }
          setIsEditingSubject(false);
        } else {
          setSubjects([
            ...subjects,
            {
              subject: formik.values.subject,
              grade: formik.values.grade,
            },
          ]);
          formik.setFieldValue("subject", "");
          formik.setFieldValue("grade", "");
        }
      }
    }
  };

  const editQualification = (item: Qualification) => {
    //setIsEditingQualification(true);
    formik.setFieldValue("qualificationType", item.qualificationType);
    formik.setFieldValue("examNumber", item.examNumber);
    setSubjects(item.subjects);
  };

  const handleSubmitForm = () => {
    formik.setFieldValue("subjects", subjects);
    formik.submitForm();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files;
    if (file) {
      formik.setFieldValue("certificate", file[0]);
    }
  };

  const validationSchema = object().shape({
    qualificationType: string().required("Qualification type is required"),
    examNumber: string().required("Exam number is required"),
    subject: string().when([], {
      is: () => isEditing || subjects?.length > 0,
      then: (passSchema) => passSchema,
      otherwise: (passSchema) => passSchema.required("Subject is required"),
    }),
    grade: string().when([], {
      is: () => isEditing || subjects?.length > 0,
      then: (passSchema) => passSchema,
      otherwise: (passSchema) => passSchema.required("Grade is required"),
    }),
    certificate: mixed().when([], {
      is: () => isEditing || subjects?.length > 0,
      then: (passSchema) => passSchema,
      otherwise: (passSchema) =>
        passSchema
          .required("Certificate is required")
          .test(
            "fileFormat",
            "*Only image or pdf files are allowed",
            (value) => isPdfOrImageFile(value as File | null),
          )
          .test("fileSize", "File size must be less than 3MB", (value) => {
            if (value) {
              return (value as File)?.size <= 3145728;
            }
            return true;
          }),
    }),
  });

  const formik = useFormik({
    initialValues: {
      qualificationType: qualification?.qualificationType ?? "",
      examNumber: qualification?.examNumber ?? "",
      subjects: [],
      certificateUrl: imageUrl,
      subject: "",
      grade: "",
    },
    onSubmit: (values, { resetForm }) => onSubmit(values, resetForm),
    validationSchema,
  });

  const subjectOptions = subject?.map((item) => {
    if (isSubjectLoading) {
      return <option value="">{<Spin size="small" />}</option>;
    } else if (isSubjectError) {
      return <option value="">{subjectError?.message}</option>;
    } else {
      return (
        <option key={item?.id} value={item?.subject}>
          {item?.subject}
        </option>
      );
    }
  });

  const gradeOptions = grades?.map((item) => {
    if (isGradeLoading) {
      return <option value="">{<Spin size="small" />}</option>;
    } else if (isGradeError) {
      return <option value="">{gradeError?.message}</option>;
    } else {
      return (
        <option key={item?.id} value={item?.grade}>
          {item?.grade}
        </option>
      );
    }
  });

  return (
    <FormikProvider value={formik}>
      <Form>
        {qualSubjects?.length > 0 && (
          <Table
            dataSource={qualSubjects}
            columns={columns}
            pagination={false}
            rowKey={(record) => record.id}
          />
        )}
        <br />
        <div className={styles.formWrapper}>
          <Input
            name="qualificationType"
            label="Qualification Type"
            placeholder="Enter qualification"
          />

          <Input
            name="examNumber"
            label="Exam Number"
            placeholder="Enter exam number"
          />

          <section className={styles.grid}>
            <Select
              name="subject"
              label="Subject"
              placeholder="Select subject"
              options={subjectOptions}
              onChange={formik.handleChange}
            />
            <Select
              name="grade"
              label="Grade"
              placeholder="Select grade"
              options={gradeOptions}
              onChange={formik.handleChange}
            />
          </section>

          {/* <section className={styles.grid}>
            <Input name="subject" label="Subject" placeholder="Subject" />
            <Input name="grade" label="Grade" placeholder="Enter grade" />
          </section> */}
        </div>

        <button
          type="button"
          onClick={handleAddSubject}
          className={styles.addSubject}
        >
          <span>+</span>
          <span>{isEditingSubject ? "Save" : "Add"} Subject</span>
        </button>

        <br />

        {subjects?.length > 0 && (
          <Table
            dataSource={subjects}
            columns={columns}
            pagination={false}
            rowKey={(record) => record?.subject}
          />
        )}

        <br />

        <Upload
          fileName={documentName(formik.values.certificateUrl)}
          onChange={handleFileChange}
          name="upload"
          label="Upload Certificate"
          accept={PDF_IMAGE_ACCEPT}
          allowedFormats={PDF_IMAGE_FORMAT_LABELS}
        />
        <br />
        <button
          onClick={handleSubmitForm}
          type="button"
          className={styles.addQualification}
        >
          <div>
            <span>+</span>
          </div>
          <span>
            {isEditing ? (
              <b>{isLoading ? "Saving..." : "Save Changes"}</b>
            ) : (
              <b>
                {isLoading ? "Adding Qualification..." : "Add Qualification"}
              </b>
            )}
          </span>
        </button>
      </Form>
    </FormikProvider>
  );
};

export default QualificationForm;
